import { useAuth0 } from "@auth0/auth0-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { InboxChangedMessage } from "entities/ISocketArgs";
import InboxDomain from "entities/domain/inbox";
import { conversationTemplatesMerchantQueryKey } from "hooks/queries/use-conversations";
import { useMerchant } from "hooks/queries/use-settings-merchant";
import { useWebSocket } from "hooks/use-socket";
import { useEffect } from "react";
import InboxService from "services/inbox";

export const inboxesQueryKey = (merchantId: number | null | undefined) => [
  "inboxes",
  merchantId ?? null,
];

/**
 * The merchant's Inboxes: the sidebar, the move destinations and the
 * translation of Release 1 Team links all read from here.
 */
export function useInboxes(merchantId?: number, staleTimeMs: number = 60_000) {
  const auth0Context = useAuth0();
  const { data: merchant } = useMerchant();
  const resolvedMerchantId = merchantId ?? merchant?.id;

  return useQuery<InboxDomain[], Error>({
    queryKey: inboxesQueryKey(resolvedMerchantId),
    enabled: Boolean(resolvedMerchantId),
    queryFn: async () => {
      if (!resolvedMerchantId) return [];
      return InboxService.getInboxes(auth0Context, resolvedMerchantId);
    },
    staleTime: staleTimeMs,
  });
}

/**
 * Marks every Inbox list stale (navigation and both settings lists) whenever
 * an Inbox changes. Mounted for every private page because these events
 * arrive wherever the user is: a list left cached while
 * Settings was open would otherwise come back with the old names. An inactive
 * list is only marked stale and refetches when next used. A reconnect
 * refetches too, since events sent while disconnected are lost.
 */
export function useInboxesRealtimeUpdates() {
  const queryClient = useQueryClient();
  const { data: merchant } = useMerchant();
  const { socket, addEventHandler, removeEventHandler } = useWebSocket();

  useEffect(() => {
    const handleInboxChanged = (args: InboxChangedMessage) => {
      void queryClient.invalidateQueries({
        queryKey: conversationTemplatesMerchantQueryKey(args.merchant_id),
      });
      void queryClient.invalidateQueries({
        queryKey: inboxesQueryKey(args.merchant_id),
      });
    };

    addEventHandler("inbox_created", handleInboxChanged);
    addEventHandler("inbox_updated", handleInboxChanged);
    addEventHandler("inbox_archived", handleInboxChanged);

    return () => {
      removeEventHandler("inbox_created", handleInboxChanged);
      removeEventHandler("inbox_updated", handleInboxChanged);
      removeEventHandler("inbox_archived", handleInboxChanged);
    };
  }, [queryClient, addEventHandler, removeEventHandler]);

  useEffect(() => {
    if (!socket || !merchant?.id) return;
    const refresh = () => {
      void queryClient.invalidateQueries({
        queryKey: conversationTemplatesMerchantQueryKey(merchant.id),
      });
      void queryClient.invalidateQueries(
        { queryKey: inboxesQueryKey(merchant.id) },
        { cancelRefetch: false }
      );
    };
    socket.on("connect", refresh);
    return () => {
      socket.off("connect", refresh);
    };
  }, [socket, queryClient, merchant?.id]);
}
